import { AxiosResponse } from 'axios';
import _get from 'lodash/get';
import { Event as BugsnagEvent } from '@bugsnag/js';

export const toError = (value: unknown, fallbackMessage = 'Unexpected error'): Error => {
  if (value instanceof Error) return value;

  if (typeof value === 'string') return new Error(value);

  if (value && typeof value === 'object') {
    const candidate = value as { message?: unknown };

    if (typeof candidate.message === 'string') {
      return new Error(candidate.message);
    }
  }

  return new Error(fallbackMessage);
};

export type LaravelValidationErrors = Record<string, string | string[]>;

export type ApiErrorData = {
  errors?: LaravelValidationErrors;
  error?: string;
  message?: string;
};

export type ApiError = Error & {
  status?: number;
  data?: ApiErrorData;
  response?: unknown;
};

export type ApiValidationError = ApiError & {
  status: 422;
  data: ApiErrorData & {
    errors: LaravelValidationErrors;
  };
};

export const isApiValidationError = (error: unknown): error is ApiValidationError =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === 422 &&
  'data' in error &&
  typeof error.data === 'object' &&
  error.data !== null &&
  'errors' in error.data &&
  typeof error.data.errors === 'object' &&
  error.data.errors !== null;

export const toApiError = (value: unknown, fallbackMessage: string): ApiError => {
  const error = toError(value, fallbackMessage) as ApiError;

  if (value && typeof value === 'object') {
    const metadata = value as {
      status?: number;
      data?: unknown;
      response?: {
        status?: number;
        data?: unknown;
      };
      message?: unknown;
    };

    if (typeof metadata.message === 'string') {
      error.message = metadata.message;
    }

    if (typeof metadata.status === 'number') {
      error.status = metadata.status;
    }

    if (
      metadata.data !== undefined &&
      typeof metadata.data === 'object' &&
      metadata.data !== null
    ) {
      error.data = metadata.data as ApiErrorData;
    }

    if (metadata.response !== undefined) {
      error.response = metadata.response;
    }

    if (error.status === undefined && typeof metadata.response?.status === 'number') {
      error.status = metadata.response.status;
    }

    if (
      error.data === undefined &&
      metadata.response?.data !== undefined &&
      typeof metadata.response.data === 'object' &&
      metadata.response.data !== null
    ) {
      error.data = metadata.response.data as ApiErrorData;
    }
  }

  return error;
};

export const getErrorResponseMessage = (error: AxiosResponse | null): string => {
  if (!error?.data?.errors) return '';

  return Object.keys(error.data.errors).reduce((msg, key) => {
    const errorMessages = error.data.errors[key].join('\n');
    return msg + errorMessages.replace(/\.\d+\./g, ' ') + '\n';
  }, '');
};

export const isResizeObserverWarning = (event: BugsnagEvent) => {
  if (_get(event, 'errors[0].errorMessage', '').toLowerCase().includes('resizeobserver loop')) {
    return true;
  }

  return false;
};

export const isUndefinedDefaultError = (event: BugsnagEvent) => {
  const errorMessage = _get(event, 'errors[0].errorMessage', '');

  return errorMessage.includes("Cannot read properties of undefined (reading 'default')");
};
